import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, ChevronLeft, ChevronRight, Trash2, Shield, Phone, Mail, School, Calendar, Award, ArrowUpDown } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const UserDirectory = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('joinDate');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/users?page=${page}&limit=15&search=${search}&sort_by=${sortBy}&sort_order=${sortOrder}`);
      setUsers(res.data.users || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger les utilisateurs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, sortOrder]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Supprimer ${name} ? Cette action est irreversible.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast({ title: 'Utilisateur supprime' });
      fetchUsers();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Echec', variant: 'destructive' });
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      toast({ title: `Role mis a jour: ${newRole}` });
      fetchUsers();
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="user-directory">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-pink-300" />
          <h2 className="text-xl font-bold text-white">Repertoire Utilisateurs</h2>
          <span className="px-2 py-1 bg-pink-500/20 text-pink-300 text-sm rounded-full">{total}</span>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-300" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher nom, email, tel..."
              className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 w-64"
              data-testid="user-search-input" />
          </div>
          <Button type="submit" size="sm" className="bg-pink-500/30 hover:bg-pink-500/50 text-white" data-testid="user-search-btn">
            Rechercher
          </Button>
        </form>
      </div>

      {/* Table */}
      <Card className="bg-white/5 border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-pink-200">
                <th className="p-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('firstName')}>
                  <div className="flex items-center gap-1">Nom <ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="p-3 text-left">Contact</th>
                <th className="p-3 text-left">Ecole</th>
                <th className="p-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('points')}>
                  <div className="flex items-center gap-1">Points <ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="p-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('level')}>
                  <div className="flex items-center gap-1">Niveau <ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="p-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('joinDate')}>
                  <div className="flex items-center gap-1">Inscription <ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-pink-200">Chargement...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-pink-200">Aucun utilisateur trouve</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                        {(u.firstName || '?')[0]}{(u.lastName || '?')[0]}
                      </div>
                      <div>
                        <div className="text-white font-medium">{u.firstName} {u.lastName}</div>
                        {u.currentStreak > 0 && <span className="text-orange-300 text-xs">Serie: {u.currentStreak}j</span>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-pink-200 text-xs">
                    {u.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>}
                    {u.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</div>}
                  </td>
                  <td className="p-3 text-pink-200 text-xs">
                    {u.school && <div className="flex items-center gap-1"><School className="w-3 h-3" />{u.school}</div>}
                  </td>
                  <td className="p-3">
                    <span className="text-yellow-300 font-semibold">{u.points || 0}</span>
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-200 rounded-full text-xs">Niv. {u.level || 1}</span>
                  </td>
                  <td className="p-3 text-pink-200 text-xs">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {u.joinDate ? new Date(u.joinDate).toLocaleDateString('fr-FR') : '-'}
                    </div>
                  </td>
                  <td className="p-3">
                    <select value={u.role || 'user'} onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      className="bg-white/10 border border-white/20 text-white text-xs rounded px-2 py-1"
                      data-testid={`role-select-${u.id}`}>
                      <option value="user" className="bg-purple-900">Utilisateur</option>
                      <option value="moderator" className="bg-purple-900">Moderateur</option>
                      <option value="super_admin" className="bg-purple-900">Super Admin</option>
                    </select>
                  </td>
                  <td className="p-3">
                    {u.role !== 'super_admin' && (
                      <button onClick={() => handleDelete(u.id, `${u.firstName} ${u.lastName}`)}
                        className="p-1.5 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                        data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-white/10">
            <span className="text-pink-200 text-sm">Page {page}/{totalPages} ({total} utilisateurs)</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="border-white/20 text-white hover:bg-white/10" data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="border-white/20 text-white hover:bg-white/10" data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserDirectory;
